<script lang="ts">
  import type {
    DisciplinaryCheckStatus,
    DisciplinarySalesHandling,
    LawyerPatchRequest,
    LitigationCaseLoad,
  } from '@shared/types/lawyer';
  import type { Lawyer } from '@domain/lawyer/model';
  import type { Firm } from '@domain/firm/model';
  import { firmOptionsStore } from '@entities/firm/store.svelte';
  import { withCurrentFirmOption } from '@domain/firm/firm-select-options';
  import { lawyerPositionOptionsStore } from '@entities/lawyer-position/store.svelte';
  import { hasBulkSalesExclusion } from '@domain/lawyer/sales-caution';
  import { bulkSalesFlagsOf } from '@domain/bulk-sales/labels';
  import type { BulkSalesFlags } from '@domain/bulk-sales/model';
  import BulkSalesChannelEditor from '@features/bulk-sales-channel-edit/BulkSalesChannelEditor.svelte';
  import BulkSalesDecisionBreakdown from '@features/bulk-sales-channel-edit/BulkSalesDecisionBreakdown.svelte';
  import { isLocked } from '@domain/lawyer/locked-columns';
  import { normalizeRegistrationNumber } from '@domain/lawyer/training-term-from-registration';
  import {
    addLawyerEmails,
    deleteLawyerEmail,
    patchLawyer,
    updateLawyerEmail,
  } from '@entities/lawyer/store.svelte';
  import EmailTagInput from '@shared/ui/EmailTagInput.svelte';
  import type { EmailTagAddFailure } from '@shared/ui/email-tag-input';
  import InlineField from '@shared/ui/InlineField.svelte';
  import InlineMultiSelectField from '@shared/ui/InlineMultiSelectField.svelte';
  // ソリューション利用パネルは firm-summary / lawyer-detail 両方の widget から所属
  // 事務所軸で再利用するため、widget ↔ widget の例外として明示的に許可する。
  // eslint-disable-next-line import/no-restricted-paths
  import CompetitorServicesPanel from '@widgets/competitor-services-panel/CompetitorServicesPanel.svelte';
  import {
    BAR_ASSOCIATION_DISPLAY_OPTIONS,
    COMMUNITY_TYPE_OPTIONS,
    DISCIPLINARY_CHECK_STATUS_OPTIONS,
    DISCIPLINARY_SALES_HANDLING_OPTIONS,
    LAW_SCHOOL_OPTIONS,
    LEVEL_LMH_OPTIONS,
    LITIGATION_CASE_LOAD_OPTIONS,
    MAJOR_SNS_OPTIONS,
    LAWYER_PRACTICE_AREA_OPTIONS,
    SIDE_WORK_COMPENSATION_INTENTION_OPTIONS,
    SIDE_WORK_INTENTION_OPTIONS,
    SIDE_WORK_TIME_INTENTION_OPTIONS,
    TRAINING_LOCATION_OPTIONS,
    UNIVERSITY_OPTIONS,
  } from '@shared/constants/choices';

  /**
   * 弁護士 左カラム v2 仕様
   * (docs/implementation/customer_profile_left_column_v2_spec.md):
   *
   *   基本情報 / 連絡先 / 属性 / ソリューション利用 / 発信・活動
   *
   * 代表フラグ / 専門分野 (細) / 利用 IT ツール (個別) / 個人課題 /
   * 関心ソリューション (個別) / ペルソナタイプ / 影響力スコア / BenPal親和度 /
   * 事務所値との差分メモ / システム項目一式は通常 UI から外す。
   *
   * 各項目は kind ごとに編集 affordance を付ける
   * (#562 / docs/implementation/20260614-customer-detail-inline-field-discoverability)。
   * select は large (未選択+chevron) / compact (segmented) を候補数で使い分け、
   * multi_select は compact (community_types / major_sns) / large (practice_areas)、
   * text-like (concurrent_roles / publications / speaking_events / media_exposure 等)
   * は placeholder を出す。 修習期は readonly。 保存値・API は不変。
   */

  interface Props {
    lawyer: Lawyer;
    canEdit: boolean;
    affiliatedFirm?: Firm | null;
    affiliatedFirmError?: string | null;
  }

  let {
    lawyer,
    canEdit,
    affiliatedFirm = null,
    affiliatedFirmError = null,
  }: Props = $props();

  type EditableTextKey =
    | 'position'
    | 'training_location'
    | 'registration_number'
    | 'bar_association'
    | 'direct_line'
    | 'personal_site_url'
    | 'university'
    | 'law_school'
    | 'individual_it_literacy'
    // 担当訴訟案件数 (spec 20260716 Scope B)。選択解除 (空) は backend が未設定
    // (NULL) として解釈する。
    | 'litigation_case_load'
    // 副業意向 (catalog No.104-106)。 選択肢マスタの確定値を select で受ける。
    | 'side_work_intention'
    | 'side_work_time_intention'
    | 'side_work_compensation_intention';

  type EditableNumberKey = 'bar_admission_year';

  type EditableKey = EditableTextKey | EditableNumberKey;

  async function save(field: EditableKey, next: string) {
    const body: LawyerPatchRequest = {};
    if (field === 'bar_admission_year') {
      const num = Number(next);
      if (Number.isNaN(num)) throw new Error('数値を入力してください');
      body[field] = num;
    } else if (field === 'registration_number') {
      const normalized = normalizeRegistrationNumber(next);
      if (normalized.error) throw new Error(normalized.error);
      body[field] = normalized.value;
    } else if (field === 'litigation_case_load') {
      // 選択解除 (空) は explicit null で未設定へ戻す。
      body.litigation_case_load = toLitigationCaseLoad(next);
    } else {
      body[field] = next;
    }
    await patchLawyer(lawyer.id, body);
  }

  /// 本人の一括営業チャネル除外 (spec 20260716 Scope D)。単一チェックは 1 boolean、
  /// 一括操作は 5 boolean をまとめて PATCH する (専用フィールド・理由・日付なし)。
  const bulkSalesFlags = $derived(bulkSalesFlagsOf(lawyer));
  async function saveBulkSalesFlags(patch: Partial<BulkSalesFlags>): Promise<void> {
    await patchLawyer(lawyer.id, patch);
  }

  function toLitigationCaseLoad(next: string): LitigationCaseLoad | null {
    switch (next) {
      case '':
        return null;
      case 'one_to_five':
      case 'six_to_ten':
      case 'eleven_to_twenty':
      case 'twenty_one_or_more':
      case 'does_not_handle_litigation':
        return next;
      default:
        throw new Error('担当訴訟案件数を選択してください');
    }
  }

  function toDisciplinaryCheckStatus(next: string): DisciplinaryCheckStatus {
    switch (next) {
      case 'unchecked':
      case 'none':
      case 'found':
        return next;
      default:
        throw new Error('確認状況を選択してください');
    }
  }

  function toDisciplinarySalesHandling(next: string): DisciplinarySalesHandling {
    switch (next) {
      case 'normal':
      case 'needs_review':
      case 'stop_contact':
        return next;
      default:
        throw new Error('営業取扱を選択してください');
    }
  }

  async function saveDisciplinaryCheckStatus(next: string) {
    await patchLawyer(lawyer.id, {
      disciplinary_check_status: toDisciplinaryCheckStatus(next),
    });
  }

  async function saveDisciplinarySalesHandling(next: string) {
    await patchLawyer(lawyer.id, {
      disciplinary_sales_handling: toDisciplinarySalesHandling(next),
    });
  }

  async function saveDisciplinaryNote(next: string) {
    const trimmed = next.trim();
    await patchLawyer(lawyer.id, {
      disciplinary_note: trimmed.length > 0 ? trimmed : null,
    });
  }

  /// `practice_areas` / `community_types` / `major_sns` などの JSONB array カラム用。
  /// 空配列は `null` に倒して 「未設定」 として保存する。
  async function saveMultiArray(
    field: 'practice_areas' | 'community_types' | 'major_sns',
    next: string[],
  ) {
    const body: LawyerPatchRequest = {};
    body[field] = next.length > 0 ? next : null;
    await patchLawyer(lawyer.id, body);
  }

  /// 兼任役員 / 発信・活動系の CSV 編集 JSONB array カラム用。
  /// 旧 UI と同じく 「, / 、」 区切りの文字列で入出力する。
  type CsvArrayField =
    | 'concurrent_roles'
    | 'publications'
    | 'speaking_events'
    | 'media_exposure';
  async function saveCsvArray(field: CsvArrayField, csv: string) {
    const arr = csv
      .split(/[,、]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const body: LawyerPatchRequest = {};
    body[field] = arr.length > 0 ? arr : null;
    await patchLawyer(lawyer.id, body);
  }

  /// CSV 編集対象カラム (string[]) の編集モードでの初期文字列。
  function csvArrayInitial(arr: string[] | null): string {
    return arr ? arr.join(', ') : '';
  }

  function valueOf(field: EditableKey): string | number | null {
    const v = lawyer[field];
    if (typeof v === 'string' || typeof v === 'number' || v === null) return v;
    return null;
  }

  function locked(field: EditableKey): boolean {
    return isLocked(field);
  }

  function asStringArray(value: unknown): string[] | null {
    if (!Array.isArray(value)) return null;
    const out = value.filter(
      (v): v is string => typeof v === 'string' && v.length > 0,
    );
    return out.length > 0 ? out : null;
  }

  const practiceAreas = $derived(asStringArray(lawyer.practice_areas));
  const majorSns = $derived(asStringArray(lawyer.major_sns));
  const concurrentRoles = $derived(asStringArray(lawyer.concurrent_roles));
  const communityTypes = $derived(asStringArray(lawyer.community_types));
  const publications = $derived(asStringArray(lawyer.publications));
  const speakingEvents = $derived(asStringArray(lawyer.speaking_events));
  const mediaExposure = $derived(asStringArray(lawyer.media_exposure));

  const hasBroadcastActivity = $derived(
    Boolean(publications || speakingEvents || mediaExposure),
  );

  function isBlank(value: string | null | undefined): boolean {
    return !value || value.trim().length === 0;
  }

  // 所属事務所 inline select / 役職 select の候補は entity store にロードを委譲する。
  // 旧 UI と同じく全 firms / 全役職を 1 回ロードして dropdown で選び直せるようにする。
  // firm 数が増えたら paginated picker への切替を検討する。
  //
  // firmOptionsStore は page_size 上限 (500) で取得するため、現在所属事務所が取得
  // ページに含まれないことがある。その場合 InlineField の select が firm_id (UUID) を
  // label 解決できず raw UUID を表示してしまうので、現在所属事務所を先頭に補完する
  // (#570 / spec #569)。affiliatedFirm 未取得・取得失敗の間も firm_id 一致の暫定
  // option を足し、どの状態でも UUID を主表示に出さない。保存値 lawyers.firm_id は不変。
  const firmOptions = $derived(
    withCurrentFirmOption(
      firmOptionsStore.data.map((f) => ({ value: f.id, label: f.official_name })),
      {
        affiliatedFirm,
        firmId: lawyer.firm_id,
        hasError: Boolean(affiliatedFirmError),
      },
    ),
  );
  const positionOptions = $derived(
    lawyerPositionOptionsStore.data.map((p) => ({ value: p.name, label: p.name })),
  );

  $effect(() => {
    // company 所属 (インハウス) は所属 select を出さないため firm 候補の取得は不要。
    if (lawyer.firm_id !== null) {
      void firmOptionsStore.load();
    }
    void lawyerPositionOptionsStore.load();
    return () => {
      firmOptionsStore.clear();
      lawyerPositionOptionsStore.clear();
    };
  });

  async function saveFirmId(next: string) {
    await patchLawyer(lawyer.id, { firm_id: next });
  }

  // -------- メール (lawyer_emails, CU-86eyaj73w) --------
  // 単一値の InlineField をタグ入力へ置換。valueOf() は string 前提のため
  // 通らず、emails 配列を専用 API (追加/編集/削除) で直接操作する。

  /// 追加。値ごとの成否 (added / duplicate / invalid) を表示用の理由に変換する。
  async function addEmails(values: string[]): Promise<EmailTagAddFailure[]> {
    const outcome = await addLawyerEmails(lawyer.id, values);
    return outcome.results
      .filter((result) => result.status !== 'added')
      .map((result) => ({
        value: result.input,
        reason:
          result.status === 'duplicate'
            ? '登録済みのメールです'
            : 'メールアドレスの形式が不正です',
      }));
  }

  async function editEmail(id: string, value: string): Promise<void> {
    try {
      await updateLawyerEmail(lawyer.id, id, value);
    } catch (err) {
      // 重複 (409 duplicate_email) は入力者向けの文言に読み替える。
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('duplicate_email')) {
        throw new Error('登録済みのメールと重複しています');
      }
      throw err;
    }
  }

  async function removeEmail(id: string): Promise<void> {
    await deleteLawyerEmail(lawyer.id, id);
  }
</script>

<div class="detail" data-testid="lawyer-detail-v2">
  <!-- 1. 基本情報 -->
  <section data-testid="lawyer-detail-basic">
    <h3>基本情報</h3>
    <div class="grid">
      {#if lawyer.firm_id !== null}
        <div
          class="readonly-cell firm-link-cell"
          data-testid="lawyer-detail-firm"
        >
          <span class="readonly-label">所属事務所</span>
          <span class="readonly-value firm-link-row">
            <InlineField
              label=""
              value={lawyer.firm_id}
              type="select"
              options={firmOptions}
              affordance="select"
              locked={isLocked('firm_id')}
              disabled={!canEdit}
              onSave={(next) => saveFirmId(next)}
            />
            <a
              href={`/firms/${lawyer.firm_id}`}
              class="detail-link"
              title="法律事務所詳細へ"
              data-testid="lawyer-detail-firm-link"
            >
              ↗ 詳細
            </a>
          </span>
        </div>
      {:else}
        <!-- company 所属 (インハウス): 企業名を read-only テキスト表示。
             所属の付け替えは importer 経由のみ (判断 1/7)。 -->
        <div class="readonly-cell" data-testid="lawyer-detail-company">
          <span class="readonly-label">所属企業</span>
          <span class="readonly-value">{lawyer.company_name ?? '-'}</span>
        </div>
      {/if}
      <InlineField
        label="役職"
        value={valueOf('position')}
        type="select"
        options={positionOptions}
        affordance="select"
        locked={locked('position')}
        disabled={!canEdit}
        onSave={(next) => save('position', next)}
      />
      <InlineField
        label="登録番号"
        value={valueOf('registration_number')}
        type="text"
        commitEmpty
        affordance="text"
        placeholder="登録番号を入力"
        locked={locked('registration_number')}
        disabled={!canEdit}
        onSave={(next) => save('registration_number', next)}
      />
      <InlineField
        label="弁護士会"
        value={valueOf('bar_association')}
        type="select"
        options={[...BAR_ASSOCIATION_DISPLAY_OPTIONS]}
        affordance="select"
        locked={locked('bar_association')}
        disabled={!canEdit}
        onSave={(next) => save('bar_association', next)}
      />
      <div class="readonly-cell">
        <span class="readonly-label">修習期</span>
        <span class="readonly-value">{lawyer.legal_training_term ?? '-'}</span>
      </div>
      <InlineField
        label="修習地"
        value={valueOf('training_location')}
        type="select"
        options={[...TRAINING_LOCATION_OPTIONS]}
        affordance="select"
        locked={locked('training_location')}
        disabled={!canEdit}
        onSave={(next) => save('training_location', next)}
      />
    </div>
  </section>

  <!-- 2. 連絡先 -->
  <section data-testid="lawyer-detail-contact">
    <h3>連絡先</h3>
    <div class="grid">
      <InlineMultiSelectField
        label="コミュニティ"
        value={communityTypes}
        options={[...COMMUNITY_TYPE_OPTIONS]}
        affordance="compact"
        locked={isLocked('community_types')}
        disabled={!canEdit}
        onSave={(next) => saveMultiArray('community_types', next)}
      />
      <div class="readonly-cell email-cell" data-testid="lawyer-detail-emails">
        <span class="readonly-label">メール</span>
        <EmailTagInput
          entries={lawyer.emails}
          disabled={!canEdit}
          onAdd={addEmails}
          onEdit={editEmail}
          onRemove={removeEmail}
        />
      </div>
      <InlineField
        label="直通電話/携帯"
        value={valueOf('direct_line')}
        type="text"
        affordance="text"
        placeholder="電話番号を入力"
        locked={locked('direct_line')}
        disabled={!canEdit}
        onSave={(next) => save('direct_line', next)}
      />
      <InlineMultiSelectField
        label="主要SNS"
        value={majorSns}
        options={[...MAJOR_SNS_OPTIONS]}
        affordance="compact"
        locked={isLocked('major_sns')}
        disabled={!canEdit}
        onSave={(next) => saveMultiArray('major_sns', next)}
      />
    </div>
  </section>

  <!-- 3. 属性 -->
  <section data-testid="lawyer-detail-attributes">
    <h3>属性</h3>
    <div class="grid">
      <InlineField
        label="出身大学"
        value={valueOf('university')}
        type="select"
        options={[...UNIVERSITY_OPTIONS]}
        affordance="select"
        locked={locked('university')}
        disabled={!canEdit}
        onSave={(next) => save('university', next)}
      />
      <InlineField
        label="出身ロースクール"
        value={valueOf('law_school')}
        type="select"
        options={[...LAW_SCHOOL_OPTIONS]}
        affordance="select"
        locked={locked('law_school')}
        disabled={!canEdit}
        onSave={(next) => save('law_school', next)}
      />
      <InlineField
        label="ITリテラシー（個人）"
        value={valueOf('individual_it_literacy')}
        type="select"
        options={[...LEVEL_LMH_OPTIONS]}
        affordance="select-compact"
        locked={locked('individual_it_literacy')}
        disabled={!canEdit}
        onSave={(next) => save('individual_it_literacy', next)}
      />
      <InlineField
        label="担当訴訟案件数"
        value={valueOf('litigation_case_load')}
        type="select"
        options={[...LITIGATION_CASE_LOAD_OPTIONS]}
        affordance="select"
        locked={locked('litigation_case_load')}
        disabled={!canEdit}
        onSave={(next) => save('litigation_case_load', next)}
      />
      <InlineMultiSelectField
        label="専門分野（個人）"
        value={practiceAreas}
        options={[...LAWYER_PRACTICE_AREA_OPTIONS]}
        affordance="large"
        locked={isLocked('practice_areas')}
        disabled={!canEdit}
        onSave={(next) => saveMultiArray('practice_areas', next)}
      />
      <!-- 旧 UI と同じく自由テキスト (カンマ / 読点区切り)。 表示は CSV 結合
           した文字列で読みやすくし、 保存時に配列化する。 -->
      <InlineField
        label="兼任役員"
        value={(concurrentRoles ?? []).join(', ')}
        type="text"
        affordance="text"
        placeholder="◯◯協会理事, △△NPO顧問"
        locked={isLocked('concurrent_roles')}
        disabled={!canEdit}
        onSave={(next) => saveCsvArray('concurrent_roles', next)}
      />
    </div>
  </section>

  <!-- 4. 利用ソリューション。 弁護士単位 (lawyer_id) で集約。
       customer_competitor_services の保存値を、 product_competitor_services 由来の
       自社ソリューション競合関係で派生分類する。 事務所単位は合算せず、
       事務所詳細タブで別途確認する。 -->
  <section id="competitor-solution-usage" data-testid="lawyer-detail-competitor-services">
    <CompetitorServicesPanel
      target={{ kind: 'lawyer', lawyerId: lawyer.id }}
      {canEdit}
      title="利用ソリューション (弁護士単位)"
    />
  </section>

  <!-- 5. 発信・活動 (旧 v2 で foldable 表示)。 計画書 §5-2 / 業務指摘により
       publications / speaking_events / media_exposure を InlineField text で
       編集できるようにする (兼任役員と同じ CSV 入出力)。 read-only で全て空の
       時は details を畳むが、 canEdit のときは常時開いて編集できる。 -->
  {#if hasBroadcastActivity || canEdit}
    <section data-testid="lawyer-detail-activity">
      <details open={hasBroadcastActivity || canEdit}>
        <summary><h3>発信・活動</h3></summary>
        <div class="grid">
          <InlineField
            label="著書・論文"
            value={csvArrayInitial(publications)}
            type="text"
            affordance="text"
            placeholder="◯◯ガイドブック, △△実務マニュアル"
            locked={isLocked('publications')}
            disabled={!canEdit}
            onSave={(next) => saveCsvArray('publications', next)}
          />
          <InlineField
            label="講演・セミナー"
            value={csvArrayInitial(speakingEvents)}
            type="text"
            affordance="text"
            placeholder="2025 LegalTech カンファレンス登壇 等"
            locked={isLocked('speaking_events')}
            disabled={!canEdit}
            onSave={(next) => saveCsvArray('speaking_events', next)}
          />
          <InlineField
            label="メディア露出"
            value={csvArrayInitial(mediaExposure)}
            type="text"
            affordance="text"
            placeholder="日経新聞インタビュー 等"
            locked={isLocked('media_exposure')}
            disabled={!canEdit}
            onSave={(next) => saveCsvArray('media_exposure', next)}
          />
        </div>
      </details>
    </section>
  {/if}

  <!-- 6. 副業 (catalog No.104-106)。 選択肢マスタの確定値を select で受ける。 -->
  <section data-testid="lawyer-detail-side-work">
    <h3>副業</h3>
    <div class="grid">
      <InlineField
        label="副業意向"
        value={valueOf('side_work_intention')}
        type="select"
        options={[...SIDE_WORK_INTENTION_OPTIONS]}
        affordance="select-compact"
        locked={locked('side_work_intention')}
        disabled={!canEdit}
        onSave={(next) => save('side_work_intention', next)}
      />
      <InlineField
        label="時間意向"
        value={valueOf('side_work_time_intention')}
        type="select"
        options={[...SIDE_WORK_TIME_INTENTION_OPTIONS]}
        affordance="select-compact"
        locked={locked('side_work_time_intention')}
        disabled={!canEdit}
        onSave={(next) => save('side_work_time_intention', next)}
      />
      <InlineField
        label="報酬意向"
        value={valueOf('side_work_compensation_intention')}
        type="select"
        options={[...SIDE_WORK_COMPENSATION_INTENTION_OPTIONS]}
        affordance="select-compact"
        locked={locked('side_work_compensation_intention')}
        disabled={!canEdit}
        onSave={(next) => save('side_work_compensation_intention', next)}
      />
    </div>
  </section>

  <!-- 7. 懲戒情報。営業判断用の最小シグナルのみを扱い、営業対象外とは分離する。 -->
  <section
    class:disciplinary-warning={lawyer.disciplinary_sales_handling === 'needs_review'}
    class:disciplinary-danger={lawyer.disciplinary_sales_handling === 'stop_contact'}
    data-testid="lawyer-detail-disciplinary-info"
  >
    <h3>懲戒情報</h3>
    <div class="grid">
      <InlineField
        label="確認状況"
        value={lawyer.disciplinary_check_status}
        type="select"
        options={[...DISCIPLINARY_CHECK_STATUS_OPTIONS]}
        allowEmpty={false}
        affordance="select-compact"
        locked={isLocked('disciplinary_check_status')}
        disabled={!canEdit}
        onSave={saveDisciplinaryCheckStatus}
      />
      <InlineField
        label="営業取扱"
        value={lawyer.disciplinary_sales_handling}
        type="select"
        options={[...DISCIPLINARY_SALES_HANDLING_OPTIONS]}
        allowEmpty={false}
        affordance="select-compact"
        locked={isLocked('disciplinary_sales_handling')}
        disabled={!canEdit}
        onSave={saveDisciplinarySalesHandling}
      />
      <InlineField
        label="営業判断メモ"
        value={lawyer.disciplinary_note ?? ''}
        type="textarea"
        textareaRows={3}
        commitEmpty
        affordance="text"
        placeholder="営業判断上の補足を記入"
        locked={isLocked('disciplinary_note')}
        disabled={!canEdit}
        onSave={saveDisciplinaryNote}
      />
    </div>
  </section>

  <!-- 8. 営業対象外設定 (spec 20260716「弁護士詳細」)。本人の 5 チャネルだけをここで
       編集し、現在の判定 (本人 / 現所属事務所 / 懲戒 stop_contact の OR 合成) は backend の
       派生値を read-only 表示する (frontend で合成しない)。旧営業NG理由は値があるときだけ
       read-only。競合 (事務所) とソリューション利用は別概念として混ぜない。 -->
  <section
    class:exclusion-active={hasBulkSalesExclusion(lawyer)}
    data-testid="lawyer-detail-sales-exclusion"
  >
    <h3>営業対象外設定</h3>
    <dl class="kv">
      <div>
        <dt>現在の判定</dt>
        <dd class="decision-cell">
          <BulkSalesDecisionBreakdown decision={lawyer.bulk_sales_decision} />
          {#if lawyer.firm_id}
            <a href={`/firms/${lawyer.firm_id}`} class="detail-link">事務所詳細</a>
          {/if}
        </dd>
      </div>
      <div>
        <dt>本人の営業対象外チャネル</dt>
        <dd>
          <BulkSalesChannelEditor
            flags={bulkSalesFlags}
            label="一括営業で使用しないチャネル"
            disabled={!canEdit}
            onSave={saveBulkSalesFlags}
          />
        </dd>
      </div>
      {#if !isBlank(lawyer.sales_exclusion_reason)}
        <div>
          <dt>旧営業NG理由</dt>
          <dd>
            <span class="legacy-reason" data-testid="lawyer-legacy-sales-exclusion-reason"
              >{lawyer.sales_exclusion_reason}</span
            >
          </dd>
        </div>
      {/if}
    </dl>
  </section>
</div>

<style>
  .detail {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  section {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 16px;
  }
  h3 {
    margin: 0 0 12px;
    font-size: 0.875rem;
    color: #374151;
    display: inline;
  }
  summary {
    cursor: pointer;
    list-style: none;
  }
  summary::marker {
    display: none;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  details[open] summary h3 {
    margin-bottom: 12px;
  }
  details > summary::before {
    content: '▶';
    font-size: 0.7rem;
    margin-right: 6px;
    color: #6b7280;
    transition: transform 0.15s ease;
    display: inline-block;
  }
  details[open] > summary::before {
    transform: rotate(90deg);
  }
  /* 旧 LawyerSummary の table.kv (key-value 縦テーブル) と同じ見た目に揃える。 */
  .grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px 16px;
  }
  .readonly-cell {
    display: grid;
    grid-template-columns: 160px 1fr;
    align-items: baseline;
    gap: 4px 16px;
    font-size: 0.875rem;
    padding: 4px 0;
    border-bottom: 1px solid #f3f4f6;
  }
  .readonly-label {
    color: #6b7280;
    font-weight: 700;
    font-size: 0.75rem;
  }
  .readonly-value {
    color: #111827;
    word-break: break-word;
  }
  .readonly-value a {
    color: #2563eb;
    text-decoration: none;
  }
  .readonly-value a:hover {
    text-decoration: underline;
  }
  .grid :global(.inline-field) {
    display: grid;
    grid-template-columns: 160px 1fr;
    align-items: baseline;
    gap: 4px 16px;
    padding: 4px 0;
    border-bottom: 1px solid #f3f4f6;
  }
  .grid :global(.inline-field > .field-label) {
    font-size: 0.75rem;
    color: #6b7280;
    font-weight: 700;
  }
  /* 所属事務所行: ラベル列はセル側で出し、 値列に InlineField + 詳細リンクを並べる。
     InlineField はラベル空 (label="") かつ grid を外して値だけ占有させる。 */
  .firm-link-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .firm-link-row :global(.inline-field) {
    flex: 1;
    display: block;
    padding: 0;
    border-bottom: none;
  }
  .firm-link-row :global(.inline-field > .field-label) {
    display: none;
  }
  .detail-link {
    font-size: 0.75rem;
    color: #2563eb;
    text-decoration: none;
    padding: 2px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: #fff;
    white-space: nowrap;
  }
  .detail-link:hover {
    background: #eff6ff;
    text-decoration: underline;
  }
  .kv {
    display: grid;
    grid-template-columns: 160px 1fr;
    gap: 8px 16px;
    margin: 0;
    font-size: 0.875rem;
  }
  .kv > div {
    display: contents;
  }
  .kv dt {
    color: #6b7280;
    font-weight: 700;
    font-size: 0.75rem;
    padding: 4px 0;
  }
  .kv dd {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin: 0;
    padding: 4px 0;
    color: #111827;
    min-width: 0;
  }
  .kv :global(.inline-field) {
    width: 100%;
  }
  .decision-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .legacy-reason {
    color: #4b5563;
    white-space: pre-wrap;
  }
  .disciplinary-warning {
    border-color: #fde68a;
    background: #fffdf5;
  }
  .disciplinary-danger {
    border-color: #fecaca;
    background: #fffafa;
  }
  .exclusion-active {
    border-color: #fecaca;
    background: #fffbfb;
  }
</style>
